import React, { useEffect, useState } from 'react'
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogTitle, IconButton, Menu, MenuItem, Paper, Table, TableBody, TableCell,
  TableHead, TableRow, TextField, Typography,
} from '@mui/material'
import AddIcon         from '@mui/icons-material/Add'
import MoreVertIcon    from '@mui/icons-material/MoreVert'
import EditIcon        from '@mui/icons-material/EditOutlined'
import ContentCopyIcon from '@mui/icons-material/ContentCopyOutlined'
import CancelIcon      from '@mui/icons-material/CancelOutlined'
import DeleteIcon      from '@mui/icons-material/DeleteOutlined'
import VideocamIcon    from '@mui/icons-material/Videocam'
import LocationOnIcon  from '@mui/icons-material/LocationOn'
import HowToRegIcon    from '@mui/icons-material/HowToReg'
import PlayCircleIcon  from '@mui/icons-material/PlayCircleOutlined'
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined'
import { supabase } from '../../supabaseClient'
import { readFunctionError } from '../../lib/functionError'
import ClassMeetingDialog, { MeetingFormState } from './ClassMeetingDialog'
import AttendanceDialog from './AttendanceDialog'

interface MentorOption {
  user_id:    string
  first_name: string
  last_name:  string
}

export interface MeetingRow {
  meeting_id:     string
  class_id:       string
  kind:           'group' | 'office_hours'
  scheduled_at:   string
  duration_min:   number
  format:         'in_person' | 'online' | 'simulcast'
  location:       string
  zoom_id:        string | null
  zoom_passcode:  string | null
  zoom_url:       string | null
  recording_url:  string | null
  host_mentor_id: string | null
  host_name:      string | null
  status:         'scheduled' | 'cancelled' | 'held'
  notes:          string | null
  attendance_count: number   // total attendance records (any status) — used to gate delete
  present_count:    number   // students marked present — shown in the "Attended" column
}

interface Props {
  classId:  string
  mentors:  MentorOption[]
  /** When true, renders editing controls. Mentors get a read-only schedule. */
  editable: boolean
  /** When true, expose the "Mark attendance" action per row. Staff only. */
  canMarkAttendance: boolean
  /** When set, show a "Message class" button in the section header (class-wide
   *  email lives here alongside the per-meeting reminders). */
  onMessageClass?: () => void
}

function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
    timeZone: 'America/Los_Angeles',
    timeZoneName: 'short',
  })
}

const FORMAT_LABEL: Record<string, string> = {
  in_person: 'In-person',
  online:    'Online',
  simulcast: 'Simulcast',
}

const KIND_LABEL: Record<string, string> = {
  group:        'Group',
  office_hours: 'Office Hours',
}

/**
 * The URL that launches Zoom for a meeting row.
 *
 * Prefers the stored `zoom_url`, which is the link Zoom itself issued and is the
 * only one that can carry a working `pwd` token. We never synthesise `?pwd=` from
 * `zoom_passcode`: Zoom's pwd parameter expects the encrypted token it generates,
 * not the numeric passcode a person types, so a hand-built one silently fails to
 * admit anyone. Without a stored URL we link to the plain meeting and leave the
 * passcode on screen for Zoom's own prompt.
 */
export function zoomJoinUrl(row: { zoom_url: string | null; zoom_id: string | null }): string | null {
  if (row.zoom_url) return row.zoom_url
  const digits = (row.zoom_id ?? '').replace(/\D/g, '')
  return digits ? `https://zoom.us/j/${digits}` : null
}

// Fields send-session-reminder may report as missing (class_meeting columns),
// asked for inline so the reminder can still go out.
const REMINDER_FIELD_META: Record<string, { label: string; type?: string }> = {
  scheduled_at:  { label: 'Date & time', type: 'datetime-local' },
  zoom_url:      { label: 'Zoom join link' },
  zoom_id:       { label: 'Zoom meeting ID' },
  zoom_passcode: { label: 'Zoom passcode' },
  location:      { label: 'Location' },
}

export default function ClassScheduleSection({ classId, mentors, editable, canMarkAttendance, onMessageClass }: Props) {
  const [rows,        setRows]        = useState<MeetingRow[]>([])
  const [loading,     setLoading]     = useState(true)
  const [error,       setError]       = useState<string | null>(null)
  const [dialogOpen,  setDialogOpen]  = useState(false)
  const [dialogInit,  setDialogInit]  = useState<Partial<MeetingFormState> | undefined>(undefined)
  const [dialogEdit,  setDialogEdit]  = useState(false)
  const [menuAnchor,  setMenuAnchor]  = useState<null | HTMLElement>(null)
  const [menuRow,     setMenuRow]     = useState<MeetingRow | null>(null)
  const [confirmCancel, setConfirmCancel] = useState<MeetingRow | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<MeetingRow | null>(null)
  const [attendanceFor, setAttendanceFor] = useState<MeetingRow | null>(null)
  const [actioning,     setActioning]     = useState<string | null>(null)

  // Session reminder
  const [reminderFor,     setReminderFor]     = useState<MeetingRow | null>(null)
  const [reminderCount,   setReminderCount]   = useState<number | null>(null)
  const [reminderError,   setReminderError]   = useState<string | null>(null)
  const [reminderMissing, setReminderMissing] = useState<string[] | null>(null)
  const [reminderFix,     setReminderFix]     = useState<Record<string, string>>({})
  const [reminderFixing,  setReminderFixing]  = useState(false)
  const [reminderSending, setReminderSending] = useState(false)
  const [reminderResult,  setReminderResult]  = useState<string | null>(null)
  const [reminderSubject, setReminderSubject] = useState('')
  const [reminderText,    setReminderText]    = useState('')

  async function openReminder(row: MeetingRow) {
    setMenuAnchor(null); setMenuRow(null)
    setReminderFor(row); setReminderCount(null); setReminderError(null)
    setReminderMissing(null); setReminderFix({})
    setReminderSubject(''); setReminderText('')
    const { data, error: invokeErr } = await supabase.functions.invoke('send-session-reminder', {
      body: { meeting_id: row.meeting_id, dry_run: true },
    })
    if (invokeErr) { setReminderError('Could not check recipients. Please try again.'); return }
    if (data?.ok === false) {
      if (Array.isArray(data.missing) && data.missing.length > 0) setReminderMissing(data.missing)
      else setReminderError(data.error ?? 'This meeting is missing some info.')
      return
    }
    setReminderCount(data?.recipients ?? 0)
    setReminderSubject(data?.preview_subject ?? '')
    setReminderText(data?.preview_text ?? '')
  }

  // Save the missing meeting details (so the record is fixed for good, not
  // just this email), then re-run the reminder check to build the message.
  async function saveMissingDetails() {
    if (!reminderFor || !reminderMissing) return
    const body: Record<string, unknown> = { meeting_id: reminderFor.meeting_id }
    for (const key of reminderMissing) {
      const value = (reminderFix[key] ?? '').trim()
      if (!value) return
      body[key] = key === 'scheduled_at' ? new Date(value).toISOString() : value
    }
    setReminderFixing(true)
    const { data, error: invokeErr } = await supabase.functions.invoke('update-class-meeting', { body })
    setReminderFixing(false)
    if (invokeErr || data?.error) { setReminderError(data?.error ?? 'Could not save the meeting details. Please try again.'); return }
    load()
    await openReminder(reminderFor)
  }

  async function sendReminder() {
    if (!reminderFor) return
    setReminderSending(true)
    const { data, error: invokeErr } = await supabase.functions.invoke('send-session-reminder', {
      body: { meeting_id: reminderFor.meeting_id, subject: reminderSubject, text: reminderText },
    })
    setReminderSending(false)
    if (invokeErr || data?.error) { setReminderError(data?.error ?? 'Could not send. Please try again.'); return }
    setReminderResult(`Reminder sent to ${data?.sent ?? 0} student${data?.sent === 1 ? '' : 's'} (parents CC'd). A summary was emailed to care@ardentacademy.com.`)
    setReminderFor(null)
  }

  async function load() {
    setLoading(true)
    const { data: meetings } = await supabase
      .from('class_meeting')
      .select('meeting_id, class_id, kind, scheduled_at, duration_min, format, location, zoom_id, zoom_passcode, zoom_url, recording_url, host_mentor_id, status, notes')
      .eq('class_id', classId)
      .order('scheduled_at')
    const list = (meetings ?? []) as Omit<MeetingRow, 'host_name' | 'attendance_count' | 'present_count'>[]

    // Pull host names + attendance counts in parallel
    const hostIds = Array.from(new Set(list.map(m => m.host_mentor_id).filter(Boolean) as string[]))
    let hostMap: Record<string, string> = {}
    if (hostIds.length > 0) {
      const { data: hosts } = await supabase
        .from('users')
        .select('user_id, first_name, last_name')
        .in('user_id', hostIds)
      hostMap = Object.fromEntries((hosts ?? []).map(h => [
        h.user_id,
        `${h.first_name ?? ''} ${h.last_name ?? ''}`.trim() || h.user_id,
      ]))
    }
    const meetingIds = list.map(m => m.meeting_id)
    const totalMap:   Record<string, number> = {}
    const presentMap: Record<string, number> = {}
    if (meetingIds.length > 0) {
      const { data: attendance } = await supabase
        .from('class_meeting_attendance')
        .select('meeting_id, status')
        .in('meeting_id', meetingIds)
      for (const a of attendance ?? []) {
        totalMap[a.meeting_id] = (totalMap[a.meeting_id] ?? 0) + 1
        if (a.status === 'present') presentMap[a.meeting_id] = (presentMap[a.meeting_id] ?? 0) + 1
      }
    }

    const merged: MeetingRow[] = list.map(m => ({
      ...m,
      host_name:        m.host_mentor_id ? hostMap[m.host_mentor_id] ?? null : null,
      attendance_count: totalMap[m.meeting_id]   ?? 0,
      present_count:    presentMap[m.meeting_id] ?? 0,
    }))
    setRows(merged)
    setLoading(false)
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load() }, [classId])

  function openCreate() {
    setDialogInit(undefined)
    setDialogEdit(false)
    setDialogOpen(true)
  }
  function openEdit(row: MeetingRow) {
    setDialogInit({
      meeting_id:     row.meeting_id,
      kind:           row.kind,
      scheduled_at:   row.scheduled_at,
      duration_min:   row.duration_min,
      format:         row.format,
      location:       row.location,
      zoom_id:        row.zoom_id ?? '',
      zoom_passcode:  row.zoom_passcode ?? '',
      zoom_url:       row.zoom_url ?? '',
      recording_url:  row.recording_url ?? '',
      host_mentor_id: row.host_mentor_id ?? '',
      notes:          row.notes ?? '',
    })
    setDialogEdit(true)
    setDialogOpen(true)
    setMenuAnchor(null); setMenuRow(null)
  }
  function openCopy(row: MeetingRow) {
    // Copy without meeting_id → create a new meeting prefilled from this one
    setDialogInit({
      kind:           row.kind,
      scheduled_at:   row.scheduled_at,
      duration_min:   row.duration_min,
      format:         row.format,
      location:       row.location,
      zoom_id:        row.zoom_id ?? '',
      zoom_passcode:  row.zoom_passcode ?? '',
      zoom_url:       row.zoom_url ?? '',
      host_mentor_id: row.host_mentor_id ?? '',
      notes:          row.notes ?? '',
    })
    setDialogEdit(false)
    setDialogOpen(true)
    setMenuAnchor(null); setMenuRow(null)
  }

  async function doCancel(row: MeetingRow) {
    setActioning(row.meeting_id)
    const { data, error: invokeErr } = await supabase.functions.invoke('update-class-meeting', {
      body: { meeting_id: row.meeting_id, status: 'cancelled' },
    })
    setActioning(null); setConfirmCancel(null)
    if (invokeErr) setError(await readFunctionError(invokeErr))
    else if (data?.error) setError(data.error)
    else load()
  }

  async function doDelete(row: MeetingRow) {
    setActioning(row.meeting_id)
    const { data, error: invokeErr } = await supabase.functions.invoke('delete-class-meeting', {
      body: { meeting_id: row.meeting_id },
    })
    setActioning(null); setConfirmDelete(null)
    if (invokeErr) setError(await readFunctionError(invokeErr))
    else if (data?.error) setError(data.error)
    else load()
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          Class Schedule {rows.length > 0 && `(${rows.length})`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {onMessageClass && (
            <Button variant="outlined" startIcon={<EmailOutlinedIcon />} onClick={onMessageClass} size="small">
              Message class
            </Button>
          )}
          {editable && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} size="small">
              Add meeting
            </Button>
          )}
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {reminderResult && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setReminderResult(null)}>{reminderResult}</Alert>}

      <Paper variant="outlined">
        {loading ? (
          <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
        ) : rows.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No meetings yet.</Typography>
            {editable && (
              <Typography variant="caption" color="text.secondary">
                Click "Add meeting" — check the "create next 4 weekly" box to set up a 5-week cohort in one go.
              </Typography>
            )}
          </Box>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>When</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Kind</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Format</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Host</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Attended</TableCell>
                {(editable || canMarkAttendance) && <TableCell sx={{ fontWeight: 600, width: 50 }}></TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.meeting_id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {fmtDateTime(r.scheduled_at)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {r.duration_min} min
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      label={KIND_LABEL[r.kind]}
                      color={r.kind === 'group' ? 'primary' : 'secondary'}
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {r.format === 'in_person' && <LocationOnIcon fontSize="small" sx={{ color: 'text.secondary' }} />}
                      {r.format === 'online'    && <VideocamIcon   fontSize="small" sx={{ color: 'text.secondary' }} />}
                      {r.format === 'simulcast' && (
                        <>
                          <LocationOnIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          <VideocamIcon   fontSize="small" sx={{ color: 'text.secondary' }} />
                        </>
                      )}
                      <Typography variant="body2">{FORMAT_LABEL[r.format]}</Typography>
                    </Box>
                    {(r.format === 'online' || r.format === 'simulcast') && r.zoom_id && (
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                        {zoomJoinUrl(r) ? (
                          <Typography
                            variant="caption"
                            component="a"
                            href={zoomJoinUrl(r) as string}
                            target="_blank"
                            rel="noopener noreferrer"
                            sx={{ color: 'primary.main', textDecoration: 'none' }}
                          >
                            ID {r.zoom_id}
                          </Typography>
                        ) : (
                          <>ID {r.zoom_id}</>
                        )}
                        {r.zoom_passcode && ` · Passcode ${r.zoom_passcode}`}
                      </Typography>
                    )}
                    {r.recording_url && (
                      <Typography
                        variant="caption"
                        component="a"
                        href={r.recording_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25, color: 'primary.main', textDecoration: 'none' }}
                      >
                        <PlayCircleIcon sx={{ fontSize: 14 }} /> Recording
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{r.host_name ?? '—'}</Typography>
                  </TableCell>
                  <TableCell>
                    {r.status === 'scheduled' && <Chip size="small" label="Scheduled" />}
                    {r.status === 'held'      && <Chip size="small" color="success" label="Held" />}
                    {r.status === 'cancelled' && <Chip size="small" color="error"   label="Cancelled" />}
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" color="text.secondary">
                      {r.present_count > 0 ? r.present_count : '—'}
                    </Typography>
                  </TableCell>
                  {(editable || canMarkAttendance) && (
                    <TableCell>
                      <IconButton
                        size="small"
                        disabled={actioning === r.meeting_id}
                        onClick={e => { setMenuAnchor(e.currentTarget); setMenuRow(r) }}
                      >
                        {actioning === r.meeting_id ? <CircularProgress size={16} /> : <MoreVertIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Paper>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => { setMenuAnchor(null); setMenuRow(null) }}>
        {menuRow && editable && menuRow.status === 'scheduled' && (
          <MenuItem onClick={() => menuRow && openReminder(menuRow)}>
            <EmailOutlinedIcon fontSize="small" sx={{ mr: 1.5 }} /> Send reminder
          </MenuItem>
        )}
        {menuRow && canMarkAttendance && menuRow.status !== 'cancelled' && (
          <MenuItem onClick={() => { setAttendanceFor(menuRow); setMenuAnchor(null) }}>
            <HowToRegIcon fontSize="small" sx={{ mr: 1.5 }} /> Mark attendance
          </MenuItem>
        )}
        {menuRow && editable && menuRow.status !== 'cancelled' && (
          <MenuItem onClick={() => menuRow && openEdit(menuRow)}>
            <EditIcon fontSize="small" sx={{ mr: 1.5 }} /> Edit
          </MenuItem>
        )}
        {menuRow && editable && (
          <MenuItem onClick={() => menuRow && openCopy(menuRow)}>
            <ContentCopyIcon fontSize="small" sx={{ mr: 1.5 }} /> Copy
          </MenuItem>
        )}
        {menuRow && editable && menuRow.status === 'scheduled' && (
          <MenuItem onClick={() => { setConfirmCancel(menuRow); setMenuAnchor(null) }}>
            <CancelIcon fontSize="small" sx={{ mr: 1.5 }} /> Cancel
          </MenuItem>
        )}
        {menuRow && editable && menuRow.attendance_count === 0 && (
          <MenuItem onClick={() => { setConfirmDelete(menuRow); setMenuAnchor(null) }} sx={{ color: 'error.main' }}>
            <DeleteIcon fontSize="small" sx={{ mr: 1.5 }} /> Delete (mistake)
          </MenuItem>
        )}
      </Menu>

      <AttendanceDialog
        open={!!attendanceFor}
        meetingId={attendanceFor?.meeting_id ?? null}
        classId={attendanceFor?.class_id ?? null}
        meetingKind={attendanceFor?.kind ?? null}
        meetingLabel={attendanceFor ? `${KIND_LABEL[attendanceFor.kind]} · ${fmtDateTime(attendanceFor.scheduled_at)}` : ''}
        onClose={() => setAttendanceFor(null)}
        onSaved={load}
      />

      {/* Send session reminder — editable message; asks for missing meeting info */}
      <Dialog open={!!reminderFor} onClose={reminderSending ? undefined : () => setReminderFor(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Send session reminder</DialogTitle>
        <DialogContent>
          {reminderFor && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              {KIND_LABEL[reminderFor.kind]} · {fmtDateTime(reminderFor.scheduled_at)}
            </Typography>
          )}
          {reminderError && <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setReminderError(null)}>{reminderError}</Alert>}
          {reminderMissing ? (
            <>
              <Alert severity="info" sx={{ mb: 2 }}>
                Some details this reminder needs are not on the meeting yet. Fill them in
                below — they&apos;ll be saved to the meeting, then the message will be prepared.
              </Alert>
              {reminderMissing.map(key => {
                const meta = REMINDER_FIELD_META[key] ?? { label: key }
                return (
                  <TextField
                    key={key} label={meta.label} type={meta.type ?? 'text'}
                    value={reminderFix[key] ?? ''}
                    onChange={e => setReminderFix(prev => ({ ...prev, [key]: e.target.value }))}
                    fullWidth sx={{ mb: 2 }} disabled={reminderFixing}
                    slotProps={meta.type === 'datetime-local' ? { inputLabel: { shrink: true } } : undefined}
                  />
                )
              })}
            </>
          ) : reminderCount === null ? (
            !reminderError && <Box sx={{ textAlign: 'center', py: 2 }}><CircularProgress size={22} /></Box>
          ) : (
            <>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Emails to <strong>{reminderCount} student{reminderCount === 1 ? '' : 's'}</strong>,
                CC&apos;ing each student&apos;s parent(s). A summary goes to care@ardentacademy.com.
              </Typography>
              <TextField
                label="Subject" value={reminderSubject}
                onChange={e => setReminderSubject(e.target.value)}
                fullWidth disabled={reminderSending} sx={{ mb: 2 }}
              />
              <TextField
                label="Message" value={reminderText}
                onChange={e => setReminderText(e.target.value)}
                fullWidth multiline minRows={10} maxRows={20} disabled={reminderSending}
                helperText="Edit freely — this is exactly what each student receives. [Student] is replaced with each student's first name."
                slotProps={{ input: { sx: { fontSize: 14, lineHeight: 1.6 } } }}
              />
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setReminderFor(null)} disabled={reminderSending || reminderFixing}>Cancel</Button>
          {reminderMissing ? (
            <Button
              variant="contained" onClick={saveMissingDetails}
              disabled={reminderFixing || reminderMissing.some(k => !(reminderFix[k] ?? '').trim())}
            >
              {reminderFixing ? <CircularProgress size={20} /> : 'Save details & continue'}
            </Button>
          ) : (
            <Button
              variant="contained" onClick={sendReminder}
              disabled={reminderSending || reminderCount === null || reminderCount === 0
                || !reminderSubject.trim() || !reminderText.trim()}
            >
              {reminderSending ? <CircularProgress size={20} /> : `Send to ${reminderCount ?? 0} student${reminderCount === 1 ? '' : 's'}`}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <ClassMeetingDialog
        open={dialogOpen}
        classId={classId}
        initial={dialogInit}
        editMode={dialogEdit}
        mentors={mentors}
        onClose={() => setDialogOpen(false)}
        onSaved={load}
      />

      <Dialog open={!!confirmCancel} onClose={() => setConfirmCancel(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Cancel meeting?</DialogTitle>
        <DialogContent>
          <Typography>
            The meeting will be marked cancelled. Students will see it as cancelled in their schedule.
            Existing attendance records are preserved.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmCancel(null)}>Keep</Button>
          <Button
            variant="contained" color="warning"
            onClick={() => confirmCancel && doCancel(confirmCancel)}
          >
            Cancel meeting
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!confirmDelete} onClose={() => setConfirmDelete(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Delete meeting?</DialogTitle>
        <DialogContent>
          <Typography>
            For undoing a meeting created by mistake. This permanently removes the row.
            (Real cancellations should use Cancel instead — that preserves history.)
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setConfirmDelete(null)}>Cancel</Button>
          <Button
            variant="contained" color="error"
            onClick={() => confirmDelete && doDelete(confirmDelete)}
          >
            Delete permanently
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
